import { useMemo, memo } from 'react';
import {
  Table,
  useTranslations,
  IUseTableReturn,
  IUseTableFetchReturn,
  IUseTableLocalizationReturn,
  TypographyWithTooltip,
  IMaterialTableOptions,
  getTimeFromNow,
  Grid,
  CreditCardAvatar,
  maskCardNumber,
} from '@uniqkey-frontend/shared-app';
import { GetVaultCreditCardsResponseModel } from '@uniqkey-backend-organization-web/api-client';
import { OWNERSHIP_TRANSLATION_KEYS } from '../../../constants';
import {
  IEmployeeGroupCreditCardsTableRow,
} from '../../../hooks/tables/useEmployeeGroupCreditCardsTable';

interface IEmployeeCreditCardsTableProps extends
  Omit<IUseTableReturn<IEmployeeGroupCreditCardsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<IUseTableFetchReturn<GetVaultCreditCardsResponseModel>, 'data' | 'refetch'>
{
  creditCards: IUseTableFetchReturn<GetVaultCreditCardsResponseModel>['data'];
  selectedCreditCards: IUseTableReturn<IEmployeeGroupCreditCardsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
}

const TABLE_OPTIONS: IMaterialTableOptions<IEmployeeGroupCreditCardsTableRow> = {
  selection: true,
};

const TOOLTIP_PROPS = { cursorPointer: true };

const EmployeeCreditCardsTable = (props: IEmployeeCreditCardsTableProps) => {
  const {
    creditCards: preCreditCards,
    isLoading,
    total,
    localization,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedCreditCards,
    onRowSelectionChange,
    createColumns,
  } = props;

  const { t, currentLanguage } = useTranslations();

  const creditCards = useMemo(() => preCreditCards.map((creditCard) => ({
    ...creditCard,
    lastActivity: getTimeFromNow({
      date: creditCard.activityAt,
      locale: currentLanguage,
    }),
    // TODO: remove 'as string' when BE updates interface
    maskedCardNumber: maskCardNumber(creditCard.lastDigits as string),
  })), [preCreditCards, currentLanguage]);

  const columns = useMemo(() => createColumns([
    {
      title: t('creditCardsTab.table.columns.number'),
      field: 'lastDigits',
      render: (rowData) => (
        <Grid container flexWrap="nowrap" alignItems="center">
          <CreditCardAvatar
            width={32}
            height={20}
            type={rowData.paymentSystem!}
          />
          <TypographyWithTooltip variant="caption">
            {rowData.maskedCardNumber}
          </TypographyWithTooltip>
        </Grid>
      ),
      width: '20%',
      sorting: false,
    },
    {
      title: t('creditCardsTab.table.columns.name'),
      field: 'name',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.name}
        </TypographyWithTooltip>
      ),
      width: '40%',
    },
    {
      title: t('creditCardsTab.table.columns.managedBy'),
      field: 'ownership',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {t(OWNERSHIP_TRANSLATION_KEYS[
            rowData.ownership as keyof typeof OWNERSHIP_TRANSLATION_KEYS
          ])}
        </TypographyWithTooltip>
      ),
      width: '20%',
    },
    {
      title: (
        <TypographyWithTooltip
          variant="subtitle1"
          tooltipProps={TOOLTIP_PROPS}
          lineClamp={5}
          title={t('creditCardsTab.table.columns.lastActivity.columnTooltip')}
        >
          {t('creditCardsTab.table.columns.lastActivity')}
        </TypographyWithTooltip>
      ),
      field: 'activityAt',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.lastActivity}
        </TypographyWithTooltip>
      ),
      width: '20%',
    },
  ]), [createColumns, t]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={creditCards}
      isLoading={isLoading}
      options={TABLE_OPTIONS}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedCreditCards}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      rowSelectByKey="vaultId"
      rowHeight={51}
    />
  );
};

export default memo(EmployeeCreditCardsTable);
